import { z } from 'zod';

export const TICKET_STATUSES = ['open', 'in_progress', 'done'] as const;
export type TicketStatus = (typeof TICKET_STATUSES)[number];
export const TICKET_STATUS_LABELS: Record<TicketStatus, string> = {
    open: 'Deschis',
    in_progress: 'In lucru',
    done: 'Finalizat',
};

export const TICKET_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
export type TicketPriority = (typeof TICKET_PRIORITIES)[number];
export const TICKET_PRIORITY_LABELS: Record<TicketPriority, string> = {
    low: 'Scazuta',
    normal: 'Normala',
    high: 'Ridicata',
    urgent: 'Urgenta',
};

export const TICKET_EVENT_TYPES = ['created', 'comment', 'status_change', 'assignment', 'edit'] as const;
export type TicketEventType = (typeof TICKET_EVENT_TYPES)[number];

// One field change inside an 'edit' event. Scalar fields carry from/to; the
// `labels` field carries added/removed name lists instead.
export const TicketEventChangeSchema = z.object({
    field: z.string(),
    from: z.string().nullish(),
    to: z.string().nullish(),
    added: z.array(z.string()).optional(),
    removed: z.array(z.string()).optional(),
});
export type TicketEventChange = z.infer<typeof TicketEventChangeSchema>;

export const TicketEventSchema = z.object({
    id: z.number(),
    ticketId: z.number(),
    type: z.enum(TICKET_EVENT_TYPES),
    body: z.string().nullish(),
    authorUserId: z.number().nullish(),
    authorName: z.string().nullish(),
    fromStatus: z.string().nullish(),
    toStatus: z.string().nullish(),
    meta: z.object({ changes: z.array(TicketEventChangeSchema) }).nullish(),
    createdAt: z.string().nullish(),
});
export type TicketEvent = z.infer<typeof TicketEventSchema>;

export const AttachmentSchema = z.object({
    id: z.number(),
    ticketId: z.number().nullish(),
    filename: z.string().nullish(),
    contentType: z.string().nullish(),
    size: z.number().nullish(),
    url: z.string().nullish(),
    createdByUserId: z.number().nullish(),
    createdByName: z.string().nullish(),
    createdAt: z.string().nullish(),
});
export type Attachment = z.infer<typeof AttachmentSchema>;

export const TicketLabelSchema = z.object({ id: z.number(), label: z.string() });
export type TicketLabel = z.infer<typeof TicketLabelSchema>;

export const TicketSchema = z.object({
    id: z.number(),
    title: z.string(),
    description: z.string().nullish(),
    status: z.enum(TICKET_STATUSES),
    priority: z.enum(TICKET_PRIORITIES),
    dueDate: z.string().nullish(),
    assigneeUserId: z.number().nullish(),
    assigneeName: z.string().nullish(),
    createdByUserId: z.number().nullish(),
    createdByName: z.string().nullish(),
    overdue: z.boolean().nullish(),
    archived: z.boolean().nullish(),
    archivedAt: z.string().nullish(),
    backlogged: z.boolean().nullish(),
    backloggedAt: z.string().nullish(),
    // Manual drag-order sort key (ASC, lower = higher up). Always set server-side.
    position: z.number().nullish(),
    labels: z.array(TicketLabelSchema).nullish(),
    // Comment thread, chronological — only present on non-done tickets when the
    // listing was fetched with withComments (entity preview panels).
    comments: z.array(TicketEventSchema).nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
});
export type Ticket = z.infer<typeof TicketSchema>;

export const TicketDetailSchema = TicketSchema.extend({
    events: z.array(TicketEventSchema).nullish(),
});
export type TicketDetail = z.infer<typeof TicketDetailSchema>;

export const DashboardSchema = z.object({
    counts: z.object({
        open: z.number(),
        in_progress: z.number(),
        done: z.number(),
        overdue: z.number(),
    }),
    perAssignee: z.array(
        z.object({
            assigneeUserId: z.number().nullable(),
            assigneeName: z.string().nullish(),
            outstanding: z.number(),
        }),
    ),
});
export type Dashboard = z.infer<typeof DashboardSchema>;

// Response envelopes — match the { success, data, count? } shape the v2 API uses.
export const TicketListResponseSchema = z.object({
    success: z.boolean(),
    data: z.array(TicketSchema),
    count: z.number(),
});
export const TicketResponseSchema = z.object({ success: z.boolean(), data: TicketSchema });
export const TicketDetailResponseSchema = z.object({ success: z.boolean(), data: TicketDetailSchema });
export const TicketEventSingleResponseSchema = z.object({ success: z.boolean(), data: TicketEventSchema });
export const TicketEventListResponseSchema = z.object({
    success: z.boolean(),
    data: z.array(TicketEventSchema),
    count: z.number().optional(),
});
export const AttachmentSingleResponseSchema = z.object({ success: z.boolean(), data: AttachmentSchema });
export const AttachmentListResponseSchema = z.object({
    success: z.boolean(),
    data: z.array(AttachmentSchema),
    count: z.number().optional(),
});
export const DashboardResponseSchema = z.object({ success: z.boolean(), data: DashboardSchema });

// Bulk "archive all done" result — the number of tickets archived.
export const ArchiveAllResponseSchema = z.object({
    success: z.boolean(),
    data: z.object({ archived: z.number() }),
});

export const TicketLabelResponseSchema = z.object({ success: z.boolean(), data: TicketLabelSchema });
export const LabelSuggestResponseSchema = z.object({ success: z.boolean(), data: z.array(z.string()) });

// Global label -> colour map (keys are lowercased label names).
export const LabelColorMapResponseSchema = z.object({
    success: z.boolean(),
    data: z.record(z.string()),
});
export const LabelColorResponseSchema = z.object({
    success: z.boolean(),
    data: z.object({ label: z.string(), color: z.string() }),
});

// Create/edit form. The assignee is set via the picker (setValue), not a
// registered input; dueDate carries the <input type="datetime-local"> string.
export const TicketFormSchema = z.object({
    title: z.string().min(1, 'Titlul este obligatoriu').max(200, 'Titlul are maxim 200 de caractere'),
    description: z.string().optional().or(z.literal('')),
    priority: z.enum(TICKET_PRIORITIES, { errorMap: () => ({ message: 'Alege o prioritate' }) }),
    dueDate: z.string().optional().or(z.literal('')),
    assigneeUserId: z.number().nullable(),
    assigneeName: z.string().nullable(),
    labels: z.array(z.string()),
});
export type TicketForm = z.infer<typeof TicketFormSchema>;

export const CommentFormSchema = z.object({
    body: z.string().min(1, 'Scrie un mesaj').max(5000, 'Mesajul e prea lung'),
});
export type CommentForm = z.infer<typeof CommentFormSchema>;

// Recurring-ticket templates (chores): the API cron creates a real ticket from
// each due template daily/weekly.
export const TICKET_FREQUENCIES = ['daily', 'weekly'] as const;
export type TicketFrequency = (typeof TICKET_FREQUENCIES)[number];
export const TICKET_FREQUENCY_LABELS: Record<TicketFrequency, string> = {
    daily: 'Zilnic',
    weekly: 'Saptamanal',
};
// Index = JS getDay() convention (0 = Sunday), same as the API stores.
export const DAY_OF_WEEK_LABELS = ['Duminica', 'Luni', 'Marti', 'Miercuri', 'Joi', 'Vineri', 'Sambata'] as const;

export const TicketRecurrenceSchema = z.object({
    id: z.number(),
    title: z.string(),
    description: z.string().nullish(),
    priority: z.enum(TICKET_PRIORITIES),
    frequency: z.enum(TICKET_FREQUENCIES),
    dayOfWeek: z.number().nullish(),
    dueInDays: z.number().nullish(),
    assigneeUserId: z.number().nullish(),
    assigneeName: z.string().nullish(),
    labels: z.array(z.string()).nullish(),
    active: z.boolean(),
    lastCreatedOn: z.string().nullish(),
    createdByUserId: z.number().nullish(),
    createdByName: z.string().nullish(),
    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
});
export type TicketRecurrence = z.infer<typeof TicketRecurrenceSchema>;

export const TicketRecurrenceListResponseSchema = z.object({
    success: z.boolean(),
    data: z.array(TicketRecurrenceSchema),
    count: z.number().optional(),
});
export const TicketRecurrenceResponseSchema = z.object({
    success: z.boolean(),
    data: TicketRecurrenceSchema,
});

// Recurrence create/edit form. dueInDays is a string because it comes from a
// <Select>; '' means "no due date".
export const TicketRecurrenceFormSchema = z.object({
    title: z.string().min(1, 'Titlul este obligatoriu').max(200, 'Titlul are maxim 200 de caractere'),
    description: z.string().optional().or(z.literal('')),
    priority: z.enum(TICKET_PRIORITIES, { errorMap: () => ({ message: 'Alege o prioritate' }) }),
    frequency: z.enum(TICKET_FREQUENCIES),
    dayOfWeek: z.number().nullable(),
    dueInDays: z.string(),
    assigneeUserId: z.number().nullable(),
    assigneeName: z.string().nullable(),
    labels: z.array(z.string()),
}).refine((v) => v.frequency !== 'weekly' || v.dayOfWeek != null, {
    path: ['dayOfWeek'],
    message: 'Alege ziua saptamanii',
});
export type TicketRecurrenceForm = z.infer<typeof TicketRecurrenceFormSchema>;

// Polymorphic references to other system objects. Mirror of the API's
// ITEM_TYPES (ticket-reference.model.js).
export const TICKET_REFERENCE_TYPES = ['order', 'user', 'product'] as const;
export type TicketReferenceType = (typeof TICKET_REFERENCE_TYPES)[number];
export const TICKET_REFERENCE_LABELS: Record<TicketReferenceType, string> = {
    order: 'Comanda',
    user: 'Utilizator',
    product: 'Produs',
};

export const TicketReferenceSchema = z.object({
    id: z.number(),
    ticketId: z.number().nullish(),
    itemType: z.string(),
    itemId: z.string(),
    label: z.string().nullish(),
    createdByUserId: z.number().nullish(),
    createdByName: z.string().nullish(),
    createdAt: z.string().nullish(),
});
export type TicketReference = z.infer<typeof TicketReferenceSchema>;

export const TicketReferenceSingleResponseSchema = z.object({
    success: z.boolean(),
    data: TicketReferenceSchema,
});
export const TicketReferenceListResponseSchema = z.object({
    success: z.boolean(),
    data: z.array(TicketReferenceSchema),
    count: z.number().optional(),
});

// Lightweight order row for the reference picker (GET /orders search). Only
// the fields we render are asserted; the rest of the heavy order row passes
// through untouched.
export const OrderSearchItemSchema = z
    .object({
        id: z.number(),
        orderNumber: z.string().nullish(),
        billingName: z.string().nullish(),
        deliveryName: z.string().nullish(),
        email: z.string().nullish(),
        status: z.string().nullish(),
    })
    .passthrough();
export type OrderSearchItem = z.infer<typeof OrderSearchItemSchema>;

export const OrderSearchResponseSchema = z.object({
    success: z.boolean(),
    data: z.array(OrderSearchItemSchema),
    count: z.number().optional(),
});

// Admin link for a referenced object. Plain hrefs (full navigation) so both
// legacy (/orders) and v2 (/v2/users) targets resolve regardless of router.
export function referenceHref(ref: Pick<TicketReference, 'itemType' | 'itemId'>): string | null {
    switch (ref.itemType) {
        case 'order':
            return `/orders/${ref.itemId}`;
        case 'user':
            return `/v2/users/${ref.itemId}`;
        case 'product':
            return `/v2/products/${ref.itemId}`;
        default:
            return null;
    }
}

// Client-side overdue check (live highlight without a refetch): due in the past
// and not done. Falls back to the server-provided `overdue` flag when present.
export function isOverdue(t: Pick<Ticket, 'dueDate' | 'status' | 'overdue'>): boolean {
    // Live computation first so a card going late on-screen highlights without a
    // refetch; the server flag is the floor (covers any clock skew).
    if (t.dueDate && t.status !== 'done') {
        const d = new Date(t.dueDate);
        if (!isNaN(d.getTime()) && d.getTime() < Date.now()) return true;
    }
    return !!t.overdue;
}
